import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Download, FileWarning, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { downloadBlob, fetchAssetBlob, isInlineContent, isInternalReportAsset, isProtectedAsset } from '../utils/assetOpener';

export default function AuthenticatedReportAsset() {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const reportMode = pathname.startsWith('/development-report-assets');
  const source = params.get('source') || '';
  const label = reportMode ? 'Protected report evidence' : 'Protected file';
  const fallbackName = params.get('name') || 'evidence';
  const [asset, setAsset] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    setAsset(null); setError(null);
    if (reportMode ? !isInternalReportAsset(source) : !isProtectedAsset(source)) {
      setError({ message: 'This file address is not supported.' });
      return undefined;
    }
    fetchAssetBlob(source, fallbackName).then((result) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(result.blob);
      setAsset({ ...result, objectUrl });
      if (!isInlineContent(result.contentType)) downloadBlob(result.blob, result.fileName);
    }).catch((reason) => { if (!cancelled) setError({ message: reason.message || 'This file is unavailable.', status: reason.status }); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [source, fallbackName, reportMode]);

  if (error) return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 px-6" data-testid="authenticated-asset-error">
      <div className="max-w-md text-center"><FileWarning className="mx-auto h-10 w-10 text-slate-400" /><h1 className="mt-4 text-xl font-semibold text-slate-800">Unable to open {reportMode ? 'evidence' : 'file'}</h1><p className="mt-2 text-sm text-slate-500" data-testid="authenticated-asset-error-message">{error.message}</p>{error.status === 401
        ? <Link to="/login" className="mt-4 inline-block text-sm font-medium text-emerald-700 underline" data-testid="authenticated-asset-signin-link">Sign in again</Link>
        : <Link to="/dashboard" className="mt-4 inline-block text-sm font-medium text-emerald-700 underline" data-testid="authenticated-asset-back-link">Back to PFAP Hub</Link>}</div>
    </main>
  );
  if (!asset) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500" data-testid="authenticated-asset-loading"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Opening {reportMode ? 'evidence' : 'file'}…</div>;

  const image = asset.contentType.startsWith('image/');
  const pdf = asset.contentType.startsWith('application/pdf');
  return (
    <main className="min-h-screen bg-slate-100" data-testid="authenticated-asset-viewer">
      <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3">
        <div className="min-w-0"><div className="truncate text-sm font-semibold text-slate-800" data-testid="authenticated-asset-filename">{asset.fileName}</div><div className="text-xs text-slate-400">{label}</div></div>
        <Button variant="outline" size="sm" onClick={() => downloadBlob(asset.blob, asset.fileName)} data-testid="authenticated-asset-download"><Download className="h-4 w-4" /> Download</Button>
      </header>
      {image && <div className="flex min-h-[calc(100vh-58px)] items-center justify-center p-4"><img src={asset.objectUrl} alt={asset.fileName} className="max-h-[calc(100vh-90px)] max-w-full object-contain" data-testid="authenticated-asset-image" /></div>}
      {pdf && <iframe title={asset.fileName} src={asset.objectUrl} className="h-[calc(100vh-58px)] w-full border-0" data-testid="authenticated-asset-pdf" />}
      {!image && !pdf && <div className="mx-auto max-w-lg px-6 py-20 text-center text-sm text-slate-600" data-testid="authenticated-asset-downloaded">The file download has started. Use Download to save it again.</div>}
    </main>
  );
}